import type { TJSONObject } from '@/types/json'

type TURLSearchParams = { [key: string]: string | boolean | number }
type TJSONValue = string | number | boolean | null | TJSONObject | TJSONValue[]

export function isAbortError(e: any): boolean {
  return e?.name === 'AbortError' || (e instanceof HTTPError && e.status === 403)
}

export const http = {
  get,
  post,
  postRaw,
  put,
  delete: del,
}

function get(url: string, params: TURLSearchParams, returnAsText: true, signal?: AbortSignal): Promise<string>
function get<T>(url: string, params?: TURLSearchParams, returnAsText?: false, signal?: AbortSignal): Promise<T>
function get<T>(url: string, params?: TURLSearchParams, returnAsText?: boolean, signal?: AbortSignal) {
  return httpRequest<T>(appendGetParamsIfPresent(url, params), 'GET', null, returnAsText, 0, signal)
}

function post(
  url: string,
  params: TJSONValue,
  returnAsText: true,
  timeout?: number,
  signal?: AbortSignal,
): Promise<string>
function post<T>(
  url: string,
  params?: TJSONValue,
  returnAsText?: false,
  timeout?: number,
  signal?: AbortSignal,
): Promise<T>
function post<T>(url: string, params?: TJSONValue, returnAsText?: boolean, timeout: number = 0, signal?: AbortSignal) {
  return httpRequest<T>(url, 'POST', params, returnAsText, timeout, signal)
}

function put(url: string, params: TJSONValue, returnAsText: true, signal?: AbortSignal): Promise<string>
function put<T>(url: string, params?: TJSONValue, returnAsText?: false, signal?: AbortSignal): Promise<T>
function put<T>(url: string, params?: TJSONValue, returnAsText?: boolean, signal?: AbortSignal) {
  return httpRequest<T>(url, 'PUT', params, returnAsText, 0, signal)
}

function del(url: string, returnAsText: true, signal?: AbortSignal): Promise<string>
function del<T>(url: string, returnAsText?: false, signal?: AbortSignal): Promise<T>
function del<T>(url: string, returnAsText?: boolean, signal?: AbortSignal) {
  return httpRequest<T>(url, 'DELETE', null, returnAsText, 0, signal)
}

function postRaw(url: string, body?: TJSONValue, signal?: AbortSignal): Promise<Response> {
  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json;charset=UTF-8' },
    body: body !== undefined ? JSON.stringify(body) : null,
    signal,
  })
}

async function httpRequest<T>(
  url: string,
  method: string,
  params?: TJSONValue,
  returnAsText?: boolean,
  timeout: number = 0,
  signal?: AbortSignal,
): Promise<T | string> {
  const fetchOptions: RequestInit = {
    method: method.toUpperCase(),
    headers: {
      'Content-Type': 'application/json;charset=UTF-8',
    },
    body: params ? JSON.stringify(params) : null,
  }
  const requestSignal = createRequestSignal(timeout, signal)
  const fetchPromise = fetch(url, { ...fetchOptions, signal: requestSignal })
  return handleFetchResponse<T>(fetchPromise, returnAsText)
}

function createRequestSignal(timeout: number, signal?: AbortSignal): AbortSignal | undefined {
  if (timeout <= 0) {
    return signal
  }

  const timeoutSignal = AbortSignal.timeout(timeout)
  if (!signal) {
    return timeoutSignal
  }

  if (typeof AbortSignal.any === 'function') {
    return AbortSignal.any([signal, timeoutSignal])
  }

  const controller = new AbortController()
  const abort = () => controller.abort()

  if (signal.aborted || timeoutSignal.aborted) {
    abort()
    return controller.signal
  }

  signal.addEventListener('abort', abort, { once: true })
  timeoutSignal.addEventListener('abort', abort, { once: true })
  return controller.signal
}

async function handleFetchResponse<T>(fetchPromise: Promise<Response>, returnAsText?: boolean): Promise<T | string> {
  const response = await fetchPromise
  if (!response.ok) {
    const text = await response.text()
    throw new HTTPError(response.status, text)
  }

  const contentType = response?.headers?.get('content-type')
  if (!contentType) {
    throw new Error('No content type in the response!')
  }

  if (returnAsText !== true) {
    if (response.redirected && response.url.includes('/login')) {
      throw new HTTPError(401, `Session expired, redirected to ${response.url}`)
    }
    const responseText = await response.text()
    try {
      return JSON.parse(responseText) as T
    } catch {
      console.error('Failed to parse JSON response from:', response.url)
      console.error('Response content:', responseText)
      throw new Error(`Invalid JSON response from ${response.url}`)
    }
  } else {
    return await response.text()
  }
}

function appendGetParamsIfPresent(urlString: string, params?: TURLSearchParams): string {
  if (!params) {
    return urlString
  }

  const url = new URL(window.location.origin + urlString)
  Object.entries(params).forEach((keyValPair: [string, string | boolean | number]) =>
    url.searchParams.set(keyValPair[0], keyValPair[1].toString()),
  )

  return url.toString()
}

export class HTTPError extends Error {
  constructor(
    public status: number,
    public response: string,
  ) {
    super(`HTTP Error: ${status} ${response}`)
  }
}

export function isUpgradeRequiredError(error: unknown): boolean {
  if (!(error instanceof HTTPError) || error.status !== 403) {
    return false
  }

  try {
    return JSON.parse(error.response)?.error === 'upgrade_required'
  } catch {
    return false
  }
}

export function getHttpErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof HTTPError && (error.status === 400 || error.status === 403)) {
    return readErrorMessage(error.response)
  }
  return fallback
}

export function getStatusMessage(error: unknown, fallback: string): string {
  if (!(error instanceof HTTPError)) {
    return fallback
  }

  let body: unknown
  try {
    body = JSON.parse(error.response)
  } catch {
    return fallback
  }

  if (typeof body !== 'object' || body === null || !('status' in body) || !('message' in body)) {
    return fallback
  }

  return typeof body.message === 'string' && body.message !== '' ? body.message : fallback
}

function readErrorMessage(body: string): string {
  try {
    const parsed = JSON.parse(body)
    return typeof parsed?.message === 'string' && parsed.message !== '' ? parsed.message : body
  } catch {
    return body
  }
}
